import { motion } from 'motion/react'
import { Calendar, Mail } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Seo } from '~/components/Seo'
import { PageLayout } from '~/lib/components/layouts/PageLayout'

export default function Contact() {
  return (
    <PageLayout>
      <Seo
        title="Contact | SOTA Navigator"
        description="Talk to the people who build SOTA Navigator. Book 30 minutes with Hatem Rabeh, or email us about your clinical evaluation literature review."
      />

      <div className="relative overflow-hidden bg-[#001871] text-white">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-5 mix-blend-soft-light"></div>
        <div className="absolute inset-0 bg-linear-to-b from-[#001871] via-[#001871] to-[#001871]/90"></div>

        <div className="relative py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-4xl">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className="space-y-8 text-center"
              >
                <h1 className="text-4xl font-bold tracking-tight text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-7xl">
                  Get in Touch
                </h1>
                <p className="text-xl leading-8 text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-2xl">
                  We're here to help you make literature reviews better, faster, and more efficient.
                </p>
              </motion.div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 w-full rotate-180 overflow-hidden leading-none">
          <svg
            className="relative block h-[50px] w-full"
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
              className="fill-white"
            ></path>
          </svg>
        </div>
      </div>

      <section className="relative bg-white py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border border-gray-100 bg-white transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4 rounded-xl bg-[#001871]/5 p-4">
                      <Calendar className="size-10 text-[#001871]" />
                    </div>
                    <h3 className="mb-2 text-xl font-semibold text-[#001871]">Schedule a Demo</h3>
                    <p className="mb-6 text-gray-600">
                      See SOTA Navigator in action with a personalized demo tailored to your needs.
                    </p>
                    <a
                      href="https://calendly.com/hatem-rabeh/30-min-seesion-with-dr-hatem-rabeh"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-full bg-[#001871] px-6 py-3 text-white transition-colors hover:bg-[#001871]/90"
                    >
                      Book a Time
                    </a>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="h-full border border-gray-100 bg-white transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-6">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4 rounded-xl bg-[#001871]/5 p-4">
                      <Mail className="size-10 text-[#001871]" />
                    </div>
                    <h3 className="mb-2 text-xl font-semibold text-[#001871]">Email Us</h3>
                    <p className="mb-6 text-gray-600">
                      Have questions or feedback? We'd love to hear from you.
                    </p>
                    <a
                      href="mailto:admin@sotanavigator.com"
                      className="inline-flex items-center gap-2 rounded-full bg-[#001871]/5 px-6 py-3 text-[#001871] transition-colors hover:bg-[#001871]/10"
                    >
                      admin@sotanavigator.com
                    </a>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#001871] py-24 text-white">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-5 mix-blend-soft-light"></div>
        <div className="absolute inset-0 bg-linear-to-b from-[#001871] via-[#001871] to-[#001871]/90"></div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-4xl font-bold tracking-tight text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-5xl">
              Support Hours
            </h2>
            <p className="text-xl text-white/90">We're available to help during these hours:</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto max-w-3xl rounded-xl border border-white/20 bg-white/10 p-8 text-center backdrop-blur-xs"
          >
            <div className="space-y-4">
              <p className="text-lg text-white/90">
                <strong>Monday - Friday:</strong> 9:00 AM - 6:00 PM (CET)
              </p>
              <p className="text-lg text-white/90">
                <strong>Response Time:</strong> Within 24 hours on business days
              </p>
              <p className="mt-6 text-white/80">
                For urgent matters outside these hours, please email us and we'll get back to you as
                soon as possible.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="relative bg-white py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto max-w-3xl rounded-xl border border-[#001871]/10 bg-white p-8 text-center shadow-lg"
          >
            <h3 className="mb-6 text-2xl font-semibold text-[#001871]">
              Let's Make Literature Reviews Better Together
            </h3>
            <p className="mb-8 text-gray-600">
              Whether you have questions, feedback, or just want to chat about literature reviews,
              we're here to help make your research process smoother and more efficient.
            </p>
            <div className="flex justify-center">
              <a
                href="https://calendly.com/hatem-rabeh/30-min-seesion-with-dr-hatem-rabeh"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-[#001871] px-6 py-3 text-white transition-colors hover:bg-[#001871]/90"
              >
                Schedule a Call
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </PageLayout>
  )
}
